import React, { useCallback, useEffect, useState } from 'react';
import { RotateCcw } from 'react-feather';
import { Badge, Button, Card, EmptyState, Loader, Table } from '../../../../ui';
import type { BadgeTone } from '../../../../ui';
import { toast } from 'react-toastify';
import {
  TelephonyAgent,
  TelephonyCall,
  telephonyApi,
} from '../../../../Telephony/telephonyApi';
import RecordingPlayer from '../../../../Telephony/RecordingPlayer';
import { Actions } from './ui-pending';
import './CallHistory.scss';

// Telephony call statuses → badge tone (telephony-specific, so kept local).
const STATUS_TONES: Record<string, BadgeTone> = {
  COMPLETED: 'success',
  BRIDGED: 'warning',
  IN_PROGRESS: 'warning',
  RINGING: 'warning',
  INITIATED: 'warning',
  QUEUED: 'neutral',
  NO_ANSWER: 'danger',
  BUSY: 'danger',
  AGENT_NOT_CONNECTED: 'danger',
  FAILED: 'danger',
  CANCELED: 'neutral',
};

const PAGE_SIZE = 50;

const DirectionBadge: React.FC<{ direction?: string }> = ({ direction }) => {
  const inbound = direction === 'INBOUND';
  return (
    <i
      className={`fa ${inbound ? 'fa-arrow-down' : 'fa-arrow-up'} call-history__dir call-history__dir--${inbound ? 'in' : 'out'}`}
      title={inbound ? 'Incoming call' : 'Outgoing call'}
      aria-label={inbound ? 'Incoming call' : 'Outgoing call'}
    />
  );
};

/** Best-effort E.164 from whatever shape the profile stores the phone in. */
function studentE164(profileData: any): string | null {
  if (!profileData) return null;
  const cc = (profileData.countryCode ?? '').toString().trim();
  const raw = (
    profileData.mobileNumber ??
    profileData.mobile ??
    profileData.phoneNumber ??
    ''
  )
    .toString()
    .trim();
  if (!raw) return null;

  let full = raw.startsWith('+') ? raw : `${cc}${raw}`;
  full = full.replace(/[\s().-]/g, '');
  if (!full.startsWith('+')) full = `+${full}`;
  return /^\+\d{6,15}$/.test(full) ? full : null;
}

/**
 * Student profile → Call History: every telephony call to this student's
 * number, whoever placed it — Sales, Demo Ops, CX — with outcome, talk time,
 * originating tool and recording playback. Reads the platform's call log
 * filtered by the student's E.164.
 */
const CallHistory: React.FC<{ profileData: any }> = ({ profileData }) => {
  const [calls, setCalls] = useState<TelephonyCall[]>([]);
  const [agents, setAgents] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const e164 = studentE164(profileData);

  useEffect(() => {
    setPage(0);
  }, [e164]);

  useEffect(() => {
    telephonyApi<TelephonyAgent[]>('/v1/agents')
      .then((list) =>
        setAgents(Object.fromEntries(list.map((a) => [a.id, a.name]))),
      )
      .catch(() => undefined);
  }, []);

  const load = useCallback(() => {
    if (!e164) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const params = new URLSearchParams({
      customerE164: e164,
      take: String(PAGE_SIZE),
      skip: String(page * PAGE_SIZE),
    });
    telephonyApi<{ items: TelephonyCall[]; total: number }>(`/v1/calls?${params}`)
      .then((res) => {
        setCalls(res.items);
        setTotal(res.total);
      })
      .catch((err) => toast.error(`Call history: ${err.message}`))
      .finally(() => setLoading(false));
  }, [e164, page]);

  useEffect(load, [load]);


  if (!e164) {
    return (
      <EmptyState
        title="No phone number on this profile"
        description="Call history needs the student's phone number. Add one in the profile to see calls."
      />
    );
  }

  return (
    <Card
      flush
      title="Call history"
      subtitle={`All calls to ${e164}, any team`}
      actions={
        <Button size="sm" variant="secondary" icon={<RotateCcw size={14} aria-hidden />} onClick={load}>
          Refresh
        </Button>
      }
    >
      {loading ? (
        <Loader label="Loading call history…" />
      ) : calls.length === 0 ? (
        <EmptyState
          title="No calls to this student yet"
          description="Calls placed from any Artium team to this number will appear here."
        />
      ) : (
        <Table caption={`Calls to ${e164}`}>
          <thead>
            <tr>
              <th>Time</th>
              <th>Called by</th>
              <th>From number</th>
              <th>Status</th>
              <th className="ds-num">Talk time</th>
              <th>Via</th>
              <th>Recording</th>
            </tr>
          </thead>
          <tbody>
            {calls.map((c) => {
              const stored = c.recordings.find((r) => r.status === 'STORED');
              return (
                <tr key={c.id}>
                  <td className="text-nowrap">
                    {new Date(c.createdAt).toLocaleString()}
                  </td>
                  <td>{c.agentId ? agents[c.agentId] ?? '—' : '—'}</td>
                  <td className="text-nowrap">
                    <DirectionBadge direction={(c as any).direction} />
                    {c.callerIdE164}
                  </td>
                  <td>
                    <Badge tone={STATUS_TONES[c.status] ?? 'neutral'}>
                      {(c as any).direction === 'INBOUND' && c.status === 'NO_ANSWER'
                        ? 'MISSED INCOMING'
                        : c.status}
                    </Badge>
                    {c.hangupCause ? (
                      <span className="call-history__meta ds-text-subtle">
                        {c.hangupCause}
                      </span>
                    ) : null}
                    {c.disconnectedBy ? (
                      <span className="call-history__meta ds-text-subtle">
                        Ended by {c.disconnectedBy.toLowerCase()}
                      </span>
                    ) : null}
                  </td>
                  <td className="ds-num">{c.talkSeconds != null ? `${c.talkSeconds}s` : '—'}</td>
                  <td>{c.source}</td>
                  <td>
                    {stored ? (
                      <RecordingPlayer recordingId={stored.id} />
                    ) : c.recordings.length ? (
                      <span className="call-history__meta ds-text-subtle">
                        {c.recordings[0].status.toLowerCase()}
                      </span>
                    ) : (
                      <span className="ds-text-subtle">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <Actions align="between" className="call-history__pager">
        <span className="call-history__meta ds-text-subtle">
          {total > 0
            ? `Showing ${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`
            : null}
        </span>
        <div className="call-history__pager-buttons">
          <Button
            size="sm"
            variant="secondary"
            disabled={loading || page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={loading || (page + 1) * PAGE_SIZE >= total}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      </Actions>
    </Card>
  );
};

export default CallHistory;
